import { createAsyncThunk } from '@reduxjs/toolkit';
import { getActiveProjectAssignments } from 'store/assignments/services/getActiveProjectAssignments';
import {
  selectAllProjectAssignmentsLoaded,
  selectAssignmentLoadStatusByProject,
} from '../selectors/expense-tracking.selectors';

// What the project pickers hold when no single project is chosen.
const PLACEHOLDER_PROJECT_IDS = ['multiple', 'none'];

export const toProjectIdList = (projectIds) =>
  [
    ...new Set(
      (projectIds || []).filter(
        (projectId) =>
          typeof projectId === 'string' &&
          projectId !== '' &&
          !PLACEHOLDER_PROJECT_IDS.includes(projectId)
      )
    ),
  ].sort();

export const loadProjectAssignments = createAsyncThunk(
  'expenseTracking/loadProjectAssignments',
  async (projectIds) => {
    const results = await Promise.allSettled(
      projectIds.map((projectId) => getActiveProjectAssignments(projectId))
    );

    const assignments = [];
    const loadedProjectIds = [];
    const failedProjectIds = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        assignments.push(...(Array.isArray(result.value) ? result.value : []));
        loadedProjectIds.push(projectIds[index]);
      } else {
        failedProjectIds.push(projectIds[index]);
      }
    });

    return { assignments, loadedProjectIds, failedProjectIds };
  }
);

// Expense tracking loads only the open project's litterat up front; anything
// that shows another project's litterat asks for that project here. Projects
// already loaded or on their way are skipped — pending is dispatched
// synchronously, so callers in the same render never fetch a project twice.
// Company mode has loaded the whole company already, so there is nothing to do.
export const ensureProjectAssignments =
  (projectIds) => (dispatch, getState) => {
    const state = getState();

    if (selectAllProjectAssignmentsLoaded(state)) {
      return undefined;
    }

    const statusByProject = selectAssignmentLoadStatusByProject(state);
    const projectIdsToLoad = toProjectIdList(projectIds).filter(
      (projectId) =>
        statusByProject[projectId] !== 'loading' &&
        statusByProject[projectId] !== 'loaded'
    );

    if (projectIdsToLoad.length === 0) {
      return undefined;
    }

    return dispatch(loadProjectAssignments(projectIdsToLoad));
  };
